import ToolBox from "@/components/annotations/toobox/toolbox-canvas.vue";
import Loading from "@/components/loading/loading.vue";
import { Annotation } from "@/models/image-meta-data";
import { VCSLevelModel } from "@/models/title-list-model";
import { ContentTitleModule } from "@/modules/content-title";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ContainerType } from "@/services/admin";
import { ContentCMImage, ContentService, ContentTitleModel } from "@/services/content";
import { EmptyGuid } from "@/util/constants";
import { ParentType } from "@/util/enum";
import { Form, Icon, Input, Select } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { Action, ActionItem } from "commonui";
import { debounce, isUndefined, last, merge } from "lodash";
import { Component, Prop, Ref, Vue, Provide } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

const UNIT_ALL = 0;
const CONTENT_ALL = "All";

@Component({
	components: {
		Search: Input.Search,
		Select,
		Option: Select.Option,
		Form,
		FormItem: Form.Item,
		Loading,
		Action,
		ActionItem,
		Icon,
		ToolBox
	}
})
export default class ContentImageSelector extends Vue {
	@Prop({ default: "" }) versionGroupId!: string;
	@Prop({ default: () => {} }) content!: { id: string; title: string; vcsLevelId: number; parentTypeId: number };
	@Prop({ default: "" }) contentId!: string;
	@Prop({ default: 0 }) unitId!: string;
	@Prop({ default: () => [] }) units!: VCSLevelModel[];
	@Prop({ default: "" }) pageId!: string;
	@Prop({ default: false }) restrictScope!: boolean;
	@Prop() chapterIds!: string[];
	@Prop({ default: false }) isAnnotationView!: boolean;
	@Prop({ default: () => {} }) setAnnotationView!: () => void;

	@Ref() readonly selectorOpen!: boolean;
	@Ref() readonly strokeWidth!: number;
	@Ref() readonly hasStickerTool!: boolean;

	public form!: WrappedFormUtils;
	public loading: boolean = false;
	public images: ContentCMImage[] = [];
	public versionGroups = VersionGroupModule.versionGroups;
	public allUnits: VCSLevelModel[] = [];
	public titles: ContentTitleModel[] = [];
	public titlesLoading: boolean = false;
	public showVersionGroup: boolean = false;
	public canvas: any;

	private conversationStore = getModule(ConversationBuilderState);
	private signatureBase!: string;
	private signatureParams!: string;
	// private contentImageSelectorStore = getModule(ContentImageEditorState);

	private formEmitEvent = true;

	created() {
		this.allUnits = this.units;
		this.conversationStore.getDownloadSasURL().then(this.setSasUrl);
		const debouncedValueChange = debounce(this.onValuesChange, 500);
		const onValuesChange = (props, fields) => {
			if (this.formEmitEvent) {
				if (fields.versionGroupId) {
					this.setUnits(fields);
				}
				if (!isUndefined(fields.versionGroupId) || !isUndefined(fields.unitId)) {
					this.setTitles(fields);
				}
			}
			this.formEmitEvent = true;

			return debouncedValueChange();
		};
		this.form = this.$form.createForm(this, {
			name: "image-editor-form",
			onValuesChange: onValuesChange.bind(this)
		});
	}
	mounted() {
		let defaultVersionGroupId = this.versionGroupId;

		if (!defaultVersionGroupId || defaultVersionGroupId === EmptyGuid) {
			defaultVersionGroupId = this.versionGroups.find((versionGroup) => versionGroup.isPublished)?.id!;
			this.showVersionGroup = true;
		}

		if (this.pageId) {
			this.getPageImage(this.pageId, defaultVersionGroupId);
		} else {
			const contentId = this.$route.params.contentId || this.$route.params.id || this.contentId || CONTENT_ALL;

			this.form.setFieldsValue({
				unitId: this.unitId,
				versionGroupId: defaultVersionGroupId,
				keyword: null,
				contentId
			});
		}
	}

	private setSasUrl() {
		const pageSignature = this.conversationStore.downloadSASURL?.find(
			(signature) => signature.type === ContainerType.Page
		);
		if (pageSignature) {
			const splitedUrl = pageSignature.url.split("?");
			this.signatureBase = splitedUrl[0] + pageSignature.name + "/";
			this.signatureParams = "?" + splitedUrl[1];
		}
	}

	public getImageUrl(path: string) {
		return this.signatureBase + path + this.signatureParams;
	}

	public onValuesChange() {
		this.form.validateFields((err, values) => {
			const { versionGroupId, keyword, unitId, contentId } = values;
			const hasContentId = !!contentId && contentId !== CONTENT_ALL;
			if (err || (!hasContentId && !keyword)) {
				return null;
			}
			this.loading = true;
			this.images = [];
			let contentIds;
			// If the component is parent then include only linked chapters.
			if (this.content.parentTypeId === ParentType.Parent) {
				contentIds = this.chapterIds;
			} else {
				contentIds = [contentId];
			}
			ContentService.getImages(versionGroupId, keyword, unitId, contentId === CONTENT_ALL ? null : contentIds)
				.then((images) => {
					this.images = images;
					this.loading = false;
				})
				.catch(() => {
					this.loading = false;
				});
		});
	}

	private setUnits(fields: any) {
		if (this.restrictScope) {
			this.allUnits = this.units.filter((u) => u.id === +this.unitId);
			this.setDefaultUnitId();
		} else {
			const { versionGroupId } = fields;
			if (versionGroupId) {
				const versionGroup = this.versionGroups.find((v) => v.id === versionGroupId);
				this.allUnits = this.units.filter((unit) => unit.curriculumType === versionGroup?.curriculumType);
			} else {
				this.allUnits = this.units;
			}
		}
	}
	private setTitles(fields: any) {
		const { versionGroupId, unitId } = merge(this.form.getFieldsValue(), fields);
		if (unitId === UNIT_ALL) {
			this.titles = [];
		} else {
			if (this.restrictScope) {
				this.titlesLoading = false;
				this.titles = [
					{ contentId: this.content.id, title: this.content.title, unitId: this.content.vcsLevelId }
				] as ContentTitleModel[];
				this.setDefaultContentId();
			} else {
				this.titlesLoading = true;
				ContentTitleModule.getTitles({
					versionGroupId,
					unitId
				}).then(
					(titles) => {
						this.titlesLoading = false;
						if (unitId === UNIT_ALL) {
							this.titles = [];
						} else {
							this.titles = titles;
						}
						this.setDefaultContentId();
					},
					() => {
						this.titlesLoading = false;
					}
				);
			}
		}
	}

	private setDefaultContentId(): void {
		const contentId = this.form.getFieldValue("contentId");
		if (contentId) {
			const hasContentTitle = this.titles.find((title) => title.contentId === contentId);
			if (!hasContentTitle) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ contentId: CONTENT_ALL });
			}
		}
	}
	private setDefaultUnitId(): void {
		const unitId = this.form.getFieldValue("unitId");
		if (unitId) {
			const hasUnitId = this.units.find((unit) => unit.id === unitId);
			if (!hasUnitId) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ unitId: 0 });
			}
		}
	}
	private getPageImage(pageId: string, defaultVersionGroupId: string) {
		return ContentService.getPageImage(pageId).then((response) => {
			this.form.setFieldsValue({
				unitId: response.unitId,
				versionGroupId: defaultVersionGroupId,
				keyword: null,
				contentId: response.contentId
			});

			this.edit(response, true);
		});
	}

	public edit(item: ContentCMImage, applyMetaData: boolean = false) {
		const fileName = last(item.imageUrl.split("/"));
		this.$emit("edit", this.getImageUrl(item.imageUrl), fileName, "image/png", item.imageDetail, applyMetaData);
	}

	public titleSearch(value: string) {
		if (this.restrictScope) {
			return;
		}

		const { unitId, versionGroupId } = this.form.getFieldsValue();
		if (!value?.length && unitId !== UNIT_ALL) {
			ContentTitleModule.getTitles({
				versionGroupId,
				unitId
			}).then((titles) => {
				this.titles = titles;
			});
			return;
		}
		if (value?.length < 2) {
			return;
		}

		ContentTitleModule.getTitles({
			versionGroupId
		}).then((titles) => {
			this.titles = titles.filter((title) => {
				if (unitId && title.unitId !== unitId) {
					return false;
				}
				return title.title.toLowerCase().indexOf(value) >= 0;
			});
		});
	}

	public dropAnnotation(annotation: Partial<Annotation>) {
		this.$emit("addShape", annotation);
	}

	toggleShapeDrawer() {
		this.setAnnotationView();
	}

	get isAnnotationOpen() {
		return this.isAnnotationView;
	}
}
